import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Landingpage from './components/landpage/Landingpage';
import Login from './components/auth/Login';
import SignUp from './components/auth/SignUp';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import TermsConditions from './components/terms/TermsConditions';
import Status from './components/info-pages/status/Status';
import About from './components/info-pages/about/About';
import Accessibility from './components/info-pages/accessibility/Accessibility';
import Sitemap from './components/info-pages/sitemap/Sitemap';
import Resources from './components/info-pages/resources/Resources';
import UserGuide from './components/info-pages/documentation/UserGuide';
import PartnerGuide from './components/info-pages/documentation/PartnerGuide';
import QuickStart from './components/info-pages/documentation/QuickStart';
import Dashboard from './components/dashboard/Dashboard';
import Overview from './components/dashboard/views/Overview';
import ChatHistory from './components/dashboard/views/ChatHistory';
import Profile from './components/dashboard/views/Profile';
import PasswordChange from './components/dashboard/views/PasswordChange';
import AdminShell from './components/dashboard/views/admin/AdminShell';
import Settings from './components/dashboard/views/Settings';
import Documents from './components/dashboard/views/Documents';
import Imagen from './components/dashboard/views/Imagen';
import ProfitabilityIndex from './components/dashboard/views/ProfitabilityIndex';
import ProfitabilityPortfolio from './components/dashboard/views/ProfitabilityIndex/Portfolio';
import DataExtraction from './components/dashboard/views/DataExtraction';
import ProSpec360 from './components/dashboard/views/ProSpec360';
import TrainingStudio from './components/dashboard/views/TrainingStudio';
import TrainingIntelligence from './components/dashboard/views/training-intelligence/TrainingIntelligence';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import RoleBasedRoute from './components/auth/RoleBasedRoute';
import FeatureGate from './components/auth/FeatureGate';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import AgreementGateModal from './components/agreements/AgreementGateModal';
import { ScreensaverProvider } from './context/ScreensaverContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,      // 2 minutes before refetch
      gcTime: 10 * 60 * 1000,         // 10 minutes cache
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});
import { ProjectProvider } from './context/ProjectContext';
import useGATracker from './hooks/useGATracker';
import UnifiedChat from './components/dashboard/views/UnifiedChat';
import ProjectsList from './components/dashboard/views/ProjectsList';
import ProjectDetail from './components/dashboard/views/ProjectDetail';
import AcceptInvitation from './pages/AcceptInvitation';
import VerifyEmail from './pages/VerifyEmail';
import AcceptAitInvitation from './pages/AcceptAitInvitation';
import AcceptSeatInvitation from './pages/AcceptSeatInvitation';
import AcceptTrainingSeat from './pages/AcceptTrainingSeat';
import BillingRedirect from './components/dashboard/views/BillingRedirect';
import PricingPage from './components/pricing/PricingPage';
import UpgradePage from './components/dashboard/views/UpgradePage';
import CheckoutReturn from './components/pricing/CheckoutReturn';
import LockedFeaturePage from './components/dashboard/views/LockedFeaturePage';
import OutlookCallback from './pages/OutlookCallback';
import EmailDashboard from './components/dashboard/views/EmailDashboard';
import TeamChatPage from './components/team-chat/TeamChatPage';
import AitDashboard from './components/dashboard/views/AitDashboard';
import AitPropertyDetail from './components/dashboard/views/AitPropertyDetail';
import AitBuildingDetail from './components/dashboard/views/AitBuildingDetail';
import AitChatPage from './components/dashboard/views/AitChatPage';
import { AitProvider } from './context/AitContext';
import { KprosProvider } from './context/KprosContext';
import SharedSnapshot from './components/shared/SharedSnapshot';
import ClientManagerDashboard from './components/dashboard/views/ClientManagerDashboard';
import ClientInvitationPage from './pages/ClientInvitationPage';
import NotesPage from './components/dashboard/views/NotesPage';
import TasksPage from './components/dashboard/views/TasksPage';
import RequestServicesPage from './components/dashboard/views/RequestServicesPage';
import ServiceRequestInboxPage from './components/dashboard/views/ServiceRequestInboxPage';
import ManagementDashboard from './components/dashboard/views/ManagementDashboard';
import AuditTrackerDashboard from './components/audit-tracker/AuditTrackerDashboard';
import LeaderboardPage from './components/dashboard/views/LeaderboardPage';
import NewsFeedPage from './components/dashboard/views/NewsFeedPage';
import ClientAuditDashboard from './components/audit-tracker/ClientAuditDashboard';

function AppRoutes() {
  const { canAccess, currentUser } = useAuth();

  return (
    <Routes>
          {/* Public routes */}
          <Route path="/" element={<Landingpage />}>
            <Route path="login" element={<Login />} />
            <Route path="signup" element={<SignUp />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="pricing" element={<PricingPage />} />
          </Route>
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/invitations/:token" element={<AcceptInvitation />} />
          <Route path="/ait-invitations/:token" element={<AcceptAitInvitation />} />
          <Route path="/client-invitations/:token" element={<ClientInvitationPage />} />
          <Route path="/seat-invitations/:token" element={<AcceptSeatInvitation />} />
          <Route path="/training/seat-invitations/:token" element={<AcceptTrainingSeat />} />
          <Route path="/shared/snapshots/:token" element={<SharedSnapshot />} />
          <Route path="/dashboard/email/callback" element={<OutlookCallback />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/about" element={<About />} />
          <Route path="/status" element={<Status />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/docs/user-guide" element={<UserGuide />} />
          <Route path="/docs/partner-guide" element={<PartnerGuide />} />
          <Route path="/docs/quick-start" element={<QuickStart />} />

          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }>
            <Route index element={<Overview />} />
            <Route path="unified-chat" element={<UnifiedChat />} />
            <Route path="chats" element={<ChatHistory />} />
            <Route path="projects" element={<ProjectsList />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="imagen" element={
              <FeatureGate featureKey="studio">
                <Imagen />
              </FeatureGate>
            } />
            <Route path="training-studio" element={
              <FeatureGate featureKey="training_studio">
                <TrainingStudio />
              </FeatureGate>
            } />
            <Route path="training" element={
              <FeatureGate featureKey="training_intelligence">
                <TrainingIntelligence />
              </FeatureGate>
            } />
            <Route path="profitability-index" element={
              <FeatureGate featureKey="profitability">
                <ProfitabilityIndex />
              </FeatureGate>
            } />
            <Route path="profitability-index/portfolio" element={
              <FeatureGate featureKey="profitability">
                <ProfitabilityPortfolio />
              </FeatureGate>
            } />
            <Route path="data-extraction" element={
              <FeatureGate featureKey="data_extraction">
                <DataExtraction />
              </FeatureGate>
            } />
            <Route path="prospec360" element={<ProSpec360 />} />
            <Route path="team-chat" element={
              <FeatureGate featureKey="team_chat">
                <TeamChatPage />
              </FeatureGate>
            } />
            <Route path="email" element={
              <FeatureGate featureKey="email">
                <EmailDashboard />
              </FeatureGate>
            } />
            <Route path="ait" element={
              <FeatureGate featureKey="ait">
                <AitDashboard />
              </FeatureGate>
            } />
            <Route path="ait/:id" element={
              <FeatureGate featureKey="ait">
                <AitPropertyDetail />
              </FeatureGate>
            } />
            <Route path="ait/:id/buildings/:buildingId" element={
              <FeatureGate featureKey="ait">
                <AitBuildingDetail />
              </FeatureGate>
            } />
            <Route path="ait/:id/chat" element={
              <FeatureGate featureKey="ait">
                <AitChatPage />
              </FeatureGate>
            } />
            <Route path="audit-tracker" element={
              <RoleBasedRoute hasAccess={() => canAccess('audit_tracker')}>
                {currentUser?.clientMembership && !currentUser?.admin
                  ? <Navigate to="/dashboard/client-audit-tracker" replace />
                  : <AuditTrackerDashboard />}
              </RoleBasedRoute>
            } />
            <Route path="audit-tracker/:clientSlug" element={
              <RoleBasedRoute hasAccess={() => canAccess('audit_tracker')}>
                {currentUser?.clientMembership && !currentUser?.admin
                  ? <Navigate to="/dashboard/client-audit-tracker" replace />
                  : <AuditTrackerDashboard />}
              </RoleBasedRoute>
            } />
            <Route path="audit-tracker/:clientSlug/:auditId" element={
              <RoleBasedRoute hasAccess={() => canAccess('audit_tracker')}>
                {currentUser?.clientMembership && !currentUser?.admin
                  ? <Navigate to="/dashboard/client-audit-tracker" replace />
                  : <AuditTrackerDashboard />}
              </RoleBasedRoute>
            } />
            <Route path="documents" element={<Documents />} />
            <Route path="leaderboards" element={<LeaderboardPage />} />
            <Route path="notes" element={
              <FeatureGate featureKey="my_notes">
                <NotesPage />
              </FeatureGate>
            } />
            <Route path="tasks" element={
              <FeatureGate featureKey="my_tasks">
                <TasksPage />
              </FeatureGate>
            } />
            <Route path="request-services" element={<RequestServicesPage />} />
            <Route path="service-requests" element={
              <RoleBasedRoute hasAccess={() => currentUser?.admin || ['director', 'manager', 'associate'].includes(currentUser?.role)}>
                <ServiceRequestInboxPage />
              </RoleBasedRoute>
            } />
            <Route path="news" element={
              <FeatureGate featureKey="news_feed">
                <NewsFeedPage />
              </FeatureGate>
            } />
            <Route path="management" element={
              <RoleBasedRoute hasAccess={() => currentUser?.admin || ['manager', 'director', 'admin'].includes(currentUser?.role)}>
                <ManagementDashboard />
              </RoleBasedRoute>
            } />
            <Route path="profile" element={<Profile />} />
            <Route path="password-change" element={<PasswordChange />} />
            <Route path="settings" element={<Settings />} />
            <Route path="upgrade" element={<UpgradePage />} />
            <Route path="locked/:featureKey" element={<LockedFeaturePage />} />
            <Route path="billing" element={<BillingRedirect />} />
            <Route path="billing/return" element={<CheckoutReturn />} />
            <Route path="client-management" element={<ClientManagerDashboard />} />
            <Route path="client-audit-tracker" element={
              <RoleBasedRoute hasAccess={() => canAccess('audit_tracker')}>
                {!currentUser?.clientMembership || currentUser?.admin
                  ? <Navigate to="/dashboard/audit-tracker" replace />
                  : <ClientAuditDashboard />}
              </RoleBasedRoute>
            } />
            <Route path="admin/*" element={<AdminRoute><AdminShell /></AdminRoute>} />
          </Route>
        </Routes>
  );
}

function App() {
  useGATracker();

  return (
    <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <ProjectProvider>
          <AitProvider>
          <KprosProvider>
          <ScreensaverProvider>
          <AppRoutes />
          <AgreementGateModal />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          </ScreensaverProvider>
        </KprosProvider>
        </AitProvider>
        </ProjectProvider>
      </AuthProvider>
    </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;